import React, { useEffect, useState } from "react";
import { fetchArchivedTournaments } from "services/tournamentService";
import { getRegistrationsByTournament } from "services/registrationService";
import * as Sentry from "@sentry/react";
import { Link } from "react-router-dom";

const TournamentArchive = ({ locationId, isAdmin = false }) => {
  const [archivedTournaments, setArchivedTournaments] = useState([]);
  const [expandedTournamentId, setExpandedTournamentId] = useState(null);

  useEffect(() => {
    const getArchivedTournaments = async () => {
      try {
        const tournaments = await fetchArchivedTournaments(locationId);
        const sortedTournaments = tournaments.sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );

        // Fetch registrations for 'Simple' tournaments
        const tournamentsWithRegistrations = await Promise.all(
          sortedTournaments.map(async (tournament) => {
            if (tournament.type === "Simple") {
              const registrations = await getRegistrationsByTournament(
                tournament.id
              );
              return { ...tournament, registrations };
            }
            return tournament;
          })
        );

        setArchivedTournaments(tournamentsWithRegistrations);
      } catch (error) {
        Sentry.captureException(error);
        console.error("Error fetching archived tournaments:", error);
      }
    };

    getArchivedTournaments();
  }, [locationId]);

  const toggleExpand = (tournamentId) => {
    setExpandedTournamentId(
      expandedTournamentId === tournamentId ? null : tournamentId
    );
  };

  return (
    <div className="bg-gray-900 text-white p-6 shadow md:mx-auto w-full flex flex-col items-center">
      <h3 className="text-2xl font-bold text-center mb-4">
        🏆 Historique des Tournois
      </h3>
      {archivedTournaments.length > 0 ? (
        <div className="flex flex-col gap-4 justify-center items-center w-full">
          {archivedTournaments.map((tournament) => (
            <div
              key={tournament.id}
              className="rounded-md bg-gray-800 p-2 w-full md:w-1/3 flex flex-col shadow-xl"
            >
              <h2 className="text-lg md:text-xl font-bold text-amber-400 pb-2 border-b border-gray-700">
                {tournament.type && tournament.date ? (
                  <>
                    {tournament.type} —{" "}
                    {new Date(tournament.date).toLocaleDateString("fr-FR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </>
                ) : (
                  tournament.name
                )}
              </h2>
              {tournament.podiums && tournament.podiums.length > 0 ? (
                <div className="space-y-2">
                  {tournament.podiums.map((podium, index) => (
                    <div key={index} className="p-2 rounded shadow-xl">
                      <p className="font-semibold">{podium.name || ""}</p>
                      <ul className="pl-2 text-sm md:text-md">
                        {podium.first && <li>🥇 {podium.first}</li>}
                        {podium.second && <li>🥈 {podium.second}</li>}
                        {podium.third && <li>🥉 {podium.third}</li>}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400">Aucun podium enregistré.</p>
              )}
              {tournament.type === "Simple" &&
              tournament.registrations &&
              tournament.registrations.length > 0 ? (
                <button
                  onClick={() => toggleExpand(tournament.id)}
                  className="mt-4 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-md"
                >
                  {expandedTournamentId === tournament.id
                    ? "Masquer les joueurs"
                    : "Voir les joueurs"}
                </button>
              ) : (
                tournament.teams &&
                Object.keys(tournament.teams).length > 0 && (
                  <button
                    onClick={() => toggleExpand(tournament.id)}
                    className="mt-4 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-md"
                  >
                    {expandedTournamentId === tournament.id
                      ? "Masquer les équipes"
                      : "Voir les équipes"}
                  </button>
                )
              )}
              {expandedTournamentId === tournament.id && (
                <div className="mt-4">
                  {tournament.type === "Simple" ? (
                    <div>
                      <h5 className="font-semibold text-start text-amber-400">
                        Joueurs
                      </h5>
                      <ol className="list-decimal pl-5 space-y-1">
                        {tournament.registrations.map((registration, idx) => (
                          <li key={idx} className="text-white">
                            {registration.name}
                          </li>
                        ))}
                      </ol>
                    </div>
                  ) : (
                    (() => {
                      const nonEmptyDivisions = Object.entries(
                        tournament.teams
                      ).filter(([, teams]) => teams && teams.length > 0);

                      return nonEmptyDivisions.map(([division, teams]) => (
                        <div key={division} className="mb-4">
                          {nonEmptyDivisions.length > 1 && (
                            <h5 className="font-semibold text-start text-amber-400">
                              {division.toUpperCase()}
                            </h5>
                          )}
                          <ol className="list-decimal pl-5 space-y-1">
                            {teams.map((team, idx) => (
                              <li key={idx} className="text-white">
                                {team.players
                                  .map((player) => player.name)
                                  .join(" et ")}
                              </li>
                            ))}
                          </ol>
                        </div>
                      ));
                    })()
                  )}
                </div>
              )}
              {isAdmin && (
                <Link
                  to={`/locations/tournaments/${tournament.id}/progress`}
                  className="text-white hover:underline pt-2"
                >
                  Accéder au tableau des matchs
                </Link>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-400 text-center">
          Aucun tournoi archivé trouvé.
        </p>
      )}
    </div>
  );
};

export default TournamentArchive;
